import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { CalendarBlankIcon as CalendarBlank, ClockIcon as Clock, ArrowRightIcon as ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import { formatApiError } from "@/lib/utils-app";

function dateNDaysOut(offset) {
  const d = new Date();
  d.setDate(d.getDate() + offset);
  return d.toISOString().slice(0, 10);
}

/**
 * Reschedule modal — same date strip + slot picker as BookingDialog,
 * but calls POST /bookings/{id}/reschedule. No payment changes.
 */
export default function RescheduleDialog({ open, onOpenChange, booking, onRescheduled }) {
  const [date, setDate] = useState(dateNDaysOut(1));
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [picked, setPicked] = useState(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setDate(dateNDaysOut(1));
      setPicked(null);
      setReason("");
    }
  }, [open]);

  useEffect(() => {
    if (!open || !booking?.service_id || !booking?.provider_id) return;
    let cancelled = false;
    setSlotsLoading(true);
    setPicked(null);
    api
      .get(`/providers/${booking.provider_id}/slots`, {
        params: { service_id: booking.service_id, date },
      })
      .then((r) => { if (!cancelled) setSlots(r.data || []); })
      .catch(() => { if (!cancelled) setSlots([]); })
      .finally(() => { if (!cancelled) setSlotsLoading(false); });
    return () => { cancelled = true; };
  }, [open, date, booking?.service_id, booking?.provider_id]);

  const submit = async () => {
    if (!picked || !booking) return;
    setBusy(true);
    try {
      await api.post(`/bookings/${booking.id}/reschedule`, {
        new_scheduled_at: picked.start_at,
        reason,
      });
      toast.success("Booking rescheduled");
      onRescheduled?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const dateOptions = useMemo(() => Array.from({ length: 14 }, (_, i) => dateNDaysOut(i + 1)), []);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="reschedule-dialog">
        <DialogHeader>
          <DialogTitle className="font-spectral text-2xl">Reschedule booking</DialogTitle>
          <DialogDescription>
            Pick a new time. {booking?.service_name ? `Current: ${new Date(booking.scheduled_at).toLocaleString()}.` : ""} No payment change.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div>
            <Label className="text-xs uppercase tracking-wider text-[#6B6B6B] mb-2 block flex items-center gap-1.5">
              <CalendarBlank size={12} weight="duotone" /> Date
            </Label>
            <div className="flex gap-1.5 overflow-x-auto pb-1" data-testid="resched-dates">
              {dateOptions.map((d) => {
                const dt = new Date(d + "T12:00:00");
                const active = d === date;
                return (
                  <button
                    key={d}
                    type="button"
                    onClick={() => setDate(d)}
                    data-testid={`resched-date-${d}`}
                    className={`flex-shrink-0 px-3 py-2 rounded-lg border text-center transition-colors ${
                      active ? "border-gold bg-cream" : "border-[#EAEAEA] hover:border-gold/55"
                    }`}
                  >
                    <div className="text-[10px] uppercase text-[#6B6B6B] tracking-wider">
                      {dt.toLocaleDateString(undefined, { weekday: "short" })}
                    </div>
                    <div className="font-semibold text-ink text-sm leading-tight">
                      {dt.toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-[#6B6B6B] mb-2 block flex items-center gap-1.5">
              <Clock size={12} weight="duotone" /> Available times
            </Label>
            {slotsLoading ? (
              <div className="text-xs text-[#6B6B6B]">Loading slots…</div>
            ) : slots.length === 0 ? (
              <div className="text-xs text-[#6B6B6B] bg-[#FAFAFA] border border-[#EAEAEA] rounded-lg p-3" data-testid="resched-no-slots">
                No openings on this date. Try another day.
              </div>
            ) : (
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-1.5" data-testid="resched-slot-grid">
                {slots.map((s) => {
                  const active = picked?.start_at === s.start_at;
                  return (
                    <button
                      key={s.start_at}
                      type="button"
                      onClick={() => setPicked(s)}
                      data-testid={`resched-slot-${s.start_at}`}
                      className={`px-2 py-2 rounded-lg border text-[12.5px] font-medium transition-colors ${
                        active ? "border-gold bg-cream text-ink" : "border-[#EAEAEA] hover:border-gold/55"
                      }`}
                    >
                      {new Date(s.start_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-[#6B6B6B] mb-1.5 block">
              Reason (optional)
            </Label>
            <Textarea
              rows={2}
              value={reason}
              onChange={(e) => setReason(e.target.value.slice(0, 500))}
              placeholder="Conflict came up, weather, illness…"
              data-testid="resched-reason"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            onClick={submit}
            disabled={!picked || busy}
            className="rounded-full bg-ink hover:bg-crimson text-white"
            data-testid="resched-submit-btn"
          >
            {busy ? "Saving…" : <>Confirm new time <ArrowRight size={13} weight="bold" className="ml-1" /></>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
